<template>
  <div class="p-2.5">
    <h2 class="text-base font-semibold mb-2 text-center">Create  {{ schoolType === 'primary' || schoolType === 'secondary' ? 'Classrooms' : 'Levels' }}</h2>

    <div v-if="mode === 'selection' && (hasExisting || hasDefaults)" data-testid="classroom-selection">
      <p v-if="hasExisting" class="mb-2 text-gray-600 text-center text-sm">
        Your school already has these {{ unitLabel }}. Continue to use them, or add more.
      </p>
      <p v-else class="mb-2 text-gray-600 text-center text-sm">
        We have a standard set of {{ unitLabel }} for <strong>{{ schoolTypeDisplay }}</strong> schools. Would you like to use them?
      </p>

      <div class="bg-gray-50 p-2.5 rounded-lg mb-3 max-h-60 overflow-y-auto">
        <ul class="space-y-1.5">
          <li v-for="classroom in listed" :key="classroom.name"
            class="flex justify-between border-b pb-1 last:border-0">
            <span class="font-medium text-sm">{{ classroom.name }}</span>
            <span class="text-gray-500 text-xs">{{ classroom.abbreviation }}</span>
          </li>
        </ul>
      </div>

      <div class="flex justify-center gap-2">
        <Button
          :label="hasExisting ? 'Continue' : 'Yes, Use Defaults'"
          icon="pi pi-check"
          size="small"
          data-testid="classroom-use-defaults"
          :loading="isSubmitting"
          @click="createDefaults"
        />
        <Button
          :label="hasExisting ? 'Add More' : 'No, Create Manually'"
          icon="pi pi-pencil"
          size="small"
          severity="secondary"
          @click="mode = 'manual'"
        />
      </div>
    </div>

    <div v-else>
      <p class="mb-2 text-gray-600 text-xs">
        Add your classrooms below. Abbreviations will be generated automatically but can be edited.
      </p>

      <div class="space-y-2 mb-3 max-h-[60vh] overflow-y-auto pr-2">
        <div v-for="(classroom, index) in manualClassrooms" :key="index"
          class="p-2.5 border rounded-lg bg-gray-50 relative">
          <Button size="small" icon="pi pi-trash" class="p-button-rounded p-button-text p-button-danger absolute top-2 right-2"
            @click="removeRow(index)" v-if="manualClassrooms.length > 1" />

          <div class="grid grid-cols-1 md:grid-cols-2 gap-2 pr-8">
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-medium">Name</label>
              <InputText v-model="classroom.name" placeholder="e.g. 100 Level" @input="generateAbbr(index)"
                :class="{ 'p-invalid': getErrorMessage('name') }" />
              <small v-if="getErrorMessage('name')" class="text-red-500">{{ getErrorMessage('name') }}</small>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-medium">Abbreviation</label>
              <InputText v-model="classroom.abbreviation" placeholder="e.g. 100L"
                :class="{ 'p-invalid': getErrorMessage('abbreviation') }" />
              <small v-if="getErrorMessage('abbreviation')" class="text-red-500">{{ getErrorMessage('abbreviation')
              }}</small>
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-between items-center">
        <Button label="Add Another" icon="pi pi-plus" size="small" class="p-button-outlined" @click="addRow" />
        <Button label="Create Classrooms" icon="pi pi-check" size="small" @click="createManual" :loading="isSubmitting"
          :disabled="!isValid" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useUserStore } from '@/store/userStore';
import { classroomTypeDefaults } from '@/constants/general-constants';
import { useToast } from 'primevue/usetoast';
import { useFormErrors } from '@/composables/useFormErrors';
import { useSchoolMutations } from '@/composables/mutations/school/useSchoolMutations';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';

import { extractGraphQLError } from '@/utils/graphqlError';
const emit = defineEmits(['success']);
const userStore = useUserStore();
const toast = useToast();
const { createClassroom } = useSchoolMutations();
const { school, classrooms } = useSchoolQuery();

const schoolType = (userStore.currentSchool?.school_type || school.value?.school_type || 'tertiary').toLowerCase();
const schoolTypeDisplay = computed(() => schoolType.charAt(0).toUpperCase() + schoolType.slice(1));

// Check if defaults exist for this school type
const defaults = computed(() => classroomTypeDefaults[schoolType as keyof typeof classroomTypeDefaults] || []);

const hasDefaults = computed(() => defaults.value.length > 0);

// The backend creates the standard classrooms/levels when the school is created
// (SchoolRepository + config constants.school_type_classroom), so normally they
// already exist here and must not be created a second time.
const existing = computed(() => (classrooms.value ?? []).filter(Boolean) as Array<{ name: string; abbreviation?: string | null }>);
const hasExisting = computed(() => existing.value.length > 0);
const listed = computed(() => (hasExisting.value ? existing.value : defaults.value));
const unitLabel = computed(() => (schoolType === 'tertiary' ? 'levels' : 'classrooms'));

const mode = ref<'selection' | 'manual'>(hasDefaults.value ? 'selection' : 'manual');

const isSubmitting = ref(false);

const manualClassrooms = ref([
  { name: '', abbreviation: '', sequence: 1 },
]);

const addRow = () => {
  const nextSeq = manualClassrooms.value.length + 1;
  manualClassrooms.value.push({ name: '', abbreviation: '', sequence: nextSeq });
};

const removeRow = (index: number) => {
  manualClassrooms.value.splice(index, 1);
  // Re-index sequences
  manualClassrooms.value.forEach((c, i) => c.sequence = i + 1);
};

const generateAbbr = (index: number) => {
  const classroom = manualClassrooms.value[index];
  if (!classroom) return;

  const name = classroom.name;
  if (name) {
    // Simple logic: First letter of each word, or just first 3 chars if 1 word
    // Or strictly for "100 Level" -> "100L"
    if (name.toLowerCase().includes('level')) {
      classroom.abbreviation = name.replace(/[^0-9]/g, '') + 'L';
    } else {
      const words = name.split(' ');
      if (words.length > 1) {
        classroom.abbreviation = words.map(w => w[0]).join('').toUpperCase();
      } else {
        classroom.abbreviation = name.slice(0, 3).toUpperCase();
      }
    }
  }
};

const isValid = computed(() => manualClassrooms.value.every(c => c.name.trim().length > 0 && c.abbreviation.trim().length > 0));

const { serverErrors, handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();

const createDefaults = async() => {
  if (hasExisting.value) {
    emit('success');
    return;
  }

  isSubmitting.value = true;
  clearErrors();
  try {
    for (const classroom of defaults.value) {
      await createClassroom(classroom);
    }
    emit('success');
  } catch (error) {
    handleServerErrors(error);
    if (Object.keys(serverErrors.value).length === 0) {
      toast.add({ severity: 'error', summary: 'Not created', detail: extractGraphQLError(error, `We couldn't create the ${unitLabel.value}. Please try again.`), life: 6000 });
    }
  } finally {
    isSubmitting.value = false;
  }
};

const createManual = async() => {
  if (!isValid.value) return;
  isSubmitting.value = true;
  clearErrors();
  try {
    for (const classroom of manualClassrooms.value) {
      await createClassroom(classroom);
    }
    emit('success');
  } catch (error) {
    handleServerErrors(error);
    if (Object.keys(serverErrors.value).length === 0) {
      toast.add({ severity: 'error', summary: 'Not created', detail: extractGraphQLError(error, `We couldn't create the ${unitLabel.value}. Please try again.`), life: 6000 });
    }
  } finally {
    isSubmitting.value = false;
  }
};
</script>
